import {
  BILLING_PERIOD_LABELS,
  CHARGE_TYPE,
  DRAWER_MODE,
} from '@monorepo/shared/schemas/common'
import type { PlanCharge } from '@monorepo/shared/schemas/plans'
import { DollarSign, Edit, Loader2, Plus, Repeat, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { ConfirmationDialog } from '@/shared/components/ui/confirmation-dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import { toast } from '@/shared/lib/toast'
import { usePlan, useRemoveCharge } from '../../hooks/usePlans'
import { RecurringChargeDrawer } from './RecurringChargeDrawer'

interface RecurringChargesTabProps {
  planId: string
}

export function RecurringChargesTab({ planId }: RecurringChargesTabProps) {
  const { data: plan, isLoading } = usePlan(planId)
  const removeCharge = useRemoveCharge()

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingCharge, setEditingCharge] = useState<PlanCharge | undefined>(
    undefined
  )
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>(
    DRAWER_MODE.create
  )

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [chargeToDelete, setChargeToDelete] = useState<PlanCharge | undefined>(
    undefined
  )

  const handleAddClick = () => {
    setEditingCharge(undefined)
    setDrawerMode(DRAWER_MODE.create)
    setDrawerOpen(true)
  }

  const handleEditClick = (charge: PlanCharge) => {
    setEditingCharge(charge)
    setDrawerMode(DRAWER_MODE.edit)
    setDrawerOpen(true)
  }

  const handleDeleteClick = (charge: PlanCharge) => {
    setChargeToDelete(charge)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!chargeToDelete) return

    try {
      await removeCharge.mutateAsync({
        planId,
        chargeId: chargeToDelete.id,
      })
      toast.success('Recurring charge removed successfully')
      setDeleteDialogOpen(false)
      setChargeToDelete(undefined)
    } catch {
      // Error toast is handled by the mutation hook
    }
  }

  // Filter only recurring charges
  const recurringCharges =
    plan?.charges?.filter(c => c.charge_type === CHARGE_TYPE.recurring) || []

  // Helper function to format billing frequency for display
  const formatBillingFrequency = (frequency: string | null) => {
    if (!frequency) return '-'
    return (
      BILLING_PERIOD_LABELS[frequency as keyof typeof BILLING_PERIOD_LABELS] ||
      frequency
    )
  }

  return (
    <>
      <Card className='bg-transparent border-none shadow-none'>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <div>
              <CardTitle className='flex items-center gap-2'>
                <Repeat className='w-5 h-5' />
                Recurring Charges
              </CardTitle>
              <p className='text-sm text-muted-foreground mt-1'>
                Charges that are billed on a regular schedule throughout the
                subscription
              </p>
            </div>
            <Button type='button' size='sm' onClick={handleAddClick}>
              <Plus className='w-4 h-4 mr-2' />
              Add Recurring Charge
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='flex items-center justify-center py-12'>
              <Loader2 className='w-6 h-6 animate-spin mr-2 text-muted-foreground' />
              <span className='text-muted-foreground'>Loading charges...</span>
            </div>
          ) : recurringCharges.length === 0 ? (
            <div className='text-center py-12 border border-dashed rounded-lg bg-muted/20'>
              <Repeat className='w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50' />
              <p className='text-muted-foreground mb-4'>
                No recurring charges configured yet
              </p>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={handleAddClick}
              >
                <Plus className='w-4 h-4 mr-2' />
                Add Your First Recurring Charge
              </Button>
            </div>
          ) : (
            <div className='border rounded-lg overflow-hidden'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Charge Name</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Billing Frequency</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className='text-right'>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recurringCharges.map(charge => (
                    <TableRow key={charge.id}>
                      <TableCell className='font-medium'>
                        {charge.charge_name}
                      </TableCell>
                      <TableCell>
                        <Badge variant='outline' className='font-mono text-xs'>
                          {charge.charge_code}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className='flex items-center gap-1 text-sm'>
                          <DollarSign className='w-3 h-3 text-muted-foreground' />
                          <span className='font-medium'>
                            {charge.amount.toFixed(2)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant='secondary' className='capitalize'>
                          {formatBillingFrequency(charge.billing_frequency)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className='text-sm text-muted-foreground'>
                          {charge.description || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className='flex items-center justify-end gap-2'>
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            onClick={() => handleEditClick(charge)}
                          >
                            <Edit className='w-4 h-4' />
                          </Button>
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            onClick={() => handleDeleteClick(charge)}
                            disabled={removeCharge.isPending}
                          >
                            <Trash2 className='w-4 h-4 text-destructive' />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recurring Charge Drawer */}
      <RecurringChargeDrawer
        planId={planId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        editingCharge={editingCharge}
        mode={drawerMode}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title='Delete Recurring Charge'
        description='Are you sure you want to remove this charge from the plan? This action cannot be undone.'
        confirmText='Delete'
        cancelText='Cancel'
        variant='destructive'
        onConfirm={handleDeleteConfirm}
        onCancel={() => setChargeToDelete(undefined)}
      />
    </>
  )
}
